/**
 * Which dataLayer push becomes which event, and under what name.
 *
 * Split out from the bridge because this is the only part that makes a
 * judgement: it decides whether a push gets through and what it is called.
 * The bridge around it only sends. That is also why the tests live here and
 * not on the bridge.
 *
 * Two sources of names:
 *
 * 1. The fixed GA4 map. Standard e-commerce, always on once the bridge is on,
 *    because those names mean the same thing everywhere.
 * 2. The customer's own list. Their key events, named explicitly in the
 *    website settings.
 *
 * Why a list instead of "everything that comes past": a real store's dataLayer
 * is full of internal traffic (GTM lifecycle, consent pings, one view-item-list
 * per carousel). Letting all of it through would inflate the customer's event
 * count, and that is what we bill on. Someone who types a name knows what they
 * are asking for.
 */

/** GA4 event name -> our canonical name. */
export const GA4_EVENT_MAP: Record<string, string> = {
  purchase: "Purchase",
  generate_lead: "Lead",
  add_to_cart: "AddToCart",
  begin_checkout: "InitiateCheckout",
  view_item: "ViewContent",
  sign_up: "CompleteRegistration",
  search: "Search",
  add_payment_info: "AddPaymentInfo",
  add_to_wishlist: "AddToWishlist",
};

/** The canonical names the fixed GA4 map can produce. */
const BRIDGE_CANONICAL_NAMES: Record<string, true> = (() => {
  const names: Record<string, true> = {};
  for (const key of Object.keys(GA4_EVENT_MAP)) names[GA4_EVENT_MAP[key]] = true;
  return names;
})();

/**
 * Could the dataLayer bridge produce this event, if the site announces it?
 *
 * Asked by the store detector, which reads the same conversions out of the DOM
 * and is the poorer of the two sources. A dataLayer push carries the shop's own
 * numbers; the detector carries whatever a CSS selector happened to find.
 */
export const bridgeCanProduce = (canonicalName: string): boolean =>
  BRIDGE_CANONICAL_NAMES[canonicalName] === true;

/**
 * How many custom names one website may declare.
 *
 * Not because twenty is a natural boundary, but because a list that grows
 * without limit has the same effect as "catch everything", and then without
 * anyone having made that choice deliberately.
 */
export const MAX_CUSTOM_NAMES = 20;

/**
 * The strictest shape the rest of the chain accepts: `RuleEventName` in
 * schemas/websites.ts. That name travels unchanged to every platform without
 * its own mapping, so whatever comes out here has to be usable there.
 */
const MIN_LENGTH = 2;
const MAX_LENGTH = 40;

/**
 * Events of which there can be only one per page load.
 *
 * On 5 August 2026 a real WooCommerce store running Pixel Manager showed that
 * one purchase produces TWO gtag pushes: first a remarketing push without an
 * order id, then the conversion push with one. The claim used that id as its
 * key, so those were two different keys and the purchase was counted twice.
 * Twice in the customer's revenue, twice in what we bill.
 *
 * Hence an order-bound event takes its name as the key rather than its id:
 * there is at most one purchase per page, however often it is announced. For
 * AddToCart the opposite holds, because adding two products in a row is
 * ordinary behaviour and the second one must not disappear silently.
 */
const ORDER_BOUND = ["Purchase"];

/**
 * Which of the already-present pushes we may process on install.
 *
 * Replay only, never pushes that arrive afterwards. We cannot look ahead
 * there, and dropping a later push because a better one might still come is
 * exactly the silent loss we do not want.
 *
 * The rule: if an order-bound event also has a version carrying an order id,
 * only that one goes through. If there is none, the version without an id goes
 * through as normal, because failing to measure a purchase is worse than
 * measuring one you cannot recognise after a reload.
 */
export const pickFromReplay = (
  items: readonly { name: string | null; hasId: boolean }[],
): boolean[] => {
  const betterExists: Record<string, boolean> = {};
  items.forEach((item) => {
    if (!item.name || !item.hasId) return;
    if (ORDER_BOUND.indexOf(item.name) === -1) return;
    betterExists[item.name] = true;
  });

  return items.map((item) => {
    if (!item.name) return false;
    if (ORDER_BOUND.indexOf(item.name) === -1) return true;
    if (item.hasId) return true;
    return !betterExists[item.name];
  });
};

/**
 * Is there at most one of these per page, tied to an order?
 *
 * Asked by the bridge, which has to know whether a push without an order id is
 * worth waiting on. See ORDER_BOUND above for why the list exists.
 */
export const isOrderBound = (canonicalName: string): boolean =>
  ORDER_BOUND.indexOf(canonicalName) !== -1;

/** The key this conversion is claimed under. */
export const claimKeyFor = (
  canonicalName: string,
  transactionId: string | undefined,
  pageUrl: string,
): string => {
  if (ORDER_BOUND.indexOf(canonicalName) !== -1) return "order";
  return transactionId || pageUrl;
};

/** A WooCommerce add is a distinct action even when several happen on one page. */
export const authoritativeEcommerceClaimKey = (
  canonicalName: string,
  transactionId: string | undefined,
  pageUrl: string,
  actionId: string | undefined,
): string => {
  const eventId = authoritativeEcommerceEventId(canonicalName, actionId);
  if (eventId) return eventId;
  return claimKeyFor(canonicalName, transactionId, pageUrl);
};

/** Stable across the AJAX response and a later page render of the same add. */
export const authoritativeEcommerceEventId = (
  canonicalName: string, actionId: string | undefined,
): string | undefined => canonicalName === "AddToCart" && actionId
  && /^[A-Za-z0-9_-]{1,80}$/.test(actionId) ? `wc_add_${actionId}` : undefined;

/**
 * Turn a raw dataLayer name into a name we are allowed to send.
 *
 * Returns null when nothing usable is left. Null is a real answer here: better
 * to send nothing than a name the platform rejects, because that rejection
 * comes back as a delivery error nobody can place.
 */
export const sanitizeEventName = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;

  // Anything that is not a letter or digit becomes a single underscore. That
  // way "quote-requested" survives as "quote_requested" and not as
  // "quoterequested": the word boundary is the only thing keeping the name
  // readable.
  const replaced = trimmed.replace(/[^A-Za-z0-9]+/g, "_");

  // A name has to start with a letter. Stripping leading digits and
  // underscores is enough; what remains starts correctly by definition.
  const withoutLead = replaced.replace(/^[^A-Za-z]+/, "");
  const withoutTail = withoutLead.replace(/_+$/, "");
  if (withoutTail.length < MIN_LENGTH) return null;

  return withoutTail.slice(0, MAX_LENGTH);
};

/**
 * The canonical name for this push, or null when we do not want it.
 *
 * The comparison against the customer's list runs on the RAW name, not the
 * sanitized one: they copy what sits in their dataLayer, hyphens and capitals
 * included. Comparing on the sanitized form would produce "no match" for
 * exactly the name they copied, and that is a gap you cannot see from the
 * other end of the line.
 */
export const canonicalNameFor = (
  dataLayerName: string,
  customNames: readonly string[] | undefined,
): string | null => {
  // GTM's own lifecycle pushes. Those never stand for anything of the customer's.
  if (dataLayerName.indexOf("gtm.") === 0) return null;

  const standard = GA4_EVENT_MAP[dataLayerName];
  if (standard) return standard;

  return listedNameFor(dataLayerName, customNames);
};

/**
 * The same thing, but WITHOUT the GA4 map: only what the customer listed.
 *
 * For the pixel bridge, where a store announces its own conversions through
 * fbq. Applying the GA4 map there would be wrong in a way that costs money: a
 * shop calling trackCustom("add_to_cart") would map onto AddToCart, which the
 * store detector is already measuring, and we would be back to counting the
 * same thing twice. On that route only names the customer typed count.
 */
export const listedNameFor = (
  rawName: string,
  customNames: readonly string[] | undefined,
): string | null => {
  if (!customNames || customNames.length === 0) return null;
  const requested = customNames
    .slice(0, MAX_CUSTOM_NAMES)
    .some((name) => name.trim() === rawName.trim());
  if (!requested) return null;

  return sanitizeEventName(rawName);
};

/**
 * Clean up what the customer types, before storing it.
 *
 * Separated by commas or newlines, because both are what someone does when
 * copying a short list out of their GTM preview.
 */
export const readCustomNames = (input: string): string[] => {
  const names = input
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  // Drop duplicates: the same name twice means the same event twice.
  const unique: string[] = [];
  for (const name of names) {
    if (unique.indexOf(name) === -1) unique.push(name);
  }
  return unique.slice(0, MAX_CUSTOM_NAMES);
};
